<script setup>
import { ref, reactive, watch, onMounted, onUnmounted } from 'vue'
import { useToast } from 'vue-toastification'
import Signal from '@/api/Signal'
import { formatRelativeDate } from '@/lib/formatDate'

const props = defineProps({
  onQueueChanged: { type: Function, default: null },
})

const toast = useToast()

const PAGE_SIZE = 20
const POLL_INTERVAL = 4000

const tabDefs = [
  { status: 0, label: 'Pending' },
  { status: 3, label: 'Failed' },
  { status: 4, label: 'Irrelevant' },
]

const activeTab = ref(0)
const deletingId = ref(null)
const retryingId = ref(null)
let pollTimer = null

const tabs = reactive({
  0: { signals: [], total: 0, offset: 0, loading: true, loaded: false },
  3: { signals: [], total: 0, offset: 0, loading: true, loaded: false },
  4: { signals: [], total: 0, offset: 0, loading: true, loaded: false },
})

const statusConfig = {
  0: { label: 'Pending', class: 'status-pending' },
  1: { label: 'Processing', class: 'status-processing' },
  3: { label: 'Failed', class: 'status-failed' },
  4: { label: 'Irrelevant', class: 'status-irrelevant' },
}

async function fetchTab(status, { append = false } = {}) {
  const tab = tabs[status]
  tab.loading = true
  try {
    const res = await Signal.listAdmin(
      append ? tab.offset : 0,
      PAGE_SIZE,
      { status }
    )
    if (res.status === 'success') {
      if (append) {
        tab.signals.push(...(res.data.signals || []))
      } else {
        tab.signals = res.data.signals || []
        tab.offset = 0
      }
      tab.total = res.data.total ?? 0
      tab.offset = tab.signals.length
      tab.loaded = true

      // Report pending count to parent
      if (status === 0 && props.onQueueChanged) {
        props.onQueueChanged(tab.total)
      }
    }
  } finally {
    tab.loading = false
  }
}

function loadMore() {
  fetchTab(activeTab.value, { append: true })
}

function canDelete(s) {
  // Don't allow deleting feed-sourced failed/irrelevant signals — they'd be re-crawled
  if (s.source_type === 3 && (s.status === 3 || s.status === 4)) return false
  return true
}

async function retrySignal(s) {
  if (retryingId.value) return
  retryingId.value = s.id
  try {
    const res = await Signal.retry(s.id, { clearError: s.status === 3 })
    if (res.status === 'success') {
      const tab = tabs[activeTab.value]
      tab.signals = tab.signals.filter(sig => sig.id !== s.id)
      tab.total = Math.max(0, tab.total - 1)
      tab.offset = tab.signals.length
      // Increment pending tab count
      tabs[0].total += 1
      toast.success('Signal queued for reanalysis')
      if (props.onQueueChanged) {
        props.onQueueChanged(tabs[0].total)
      }
    } else {
      toast.error(res.message || 'Failed to retry signal')
    }
  } finally {
    retryingId.value = null
  }
}

async function deleteSignal(id) {
  if (deletingId.value) return
  deletingId.value = id
  try {
    const res = await Signal.delete(id)
    if (res.status === 'success') {
      const tab = tabs[activeTab.value]
      tab.signals = tab.signals.filter(s => s.id !== id)
      tab.total = Math.max(0, tab.total - 1)
      tab.offset = tab.signals.length
      toast.success('Signal removed')
      if (props.onQueueChanged) {
        props.onQueueChanged(tabs[0].total)
      }
    } else {
      toast.error(res.message || 'Failed to delete signal')
    }
  } finally {
    deletingId.value = null
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(() => fetchTab(activeTab.value), POLL_INTERVAL)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function truncateUrl(url, max = 60) {
  if (!url || url.length <= max) return url
  return url.substring(0, max) + '...'
}

watch(activeTab, (status) => {
  if (!tabs[status].loaded) {
    fetchTab(status)
  }
  startPolling()
})

onMounted(async () => {
  await fetchTab(activeTab.value)
  startPolling()
  // Pre-fetch totals for other tabs (background)
  for (const def of tabDefs) {
    if (def.status !== activeTab.value) {
      fetchTab(def.status)
    }
  }
})

onUnmounted(stopPolling)
</script>

<template>
  <div class="signal-queue">
    <!-- Tabs -->
    <div class="queue-tabs">
      <button
        v-for="def in tabDefs"
        :key="def.status"
        class="queue-tab"
        :class="{ active: activeTab === def.status }"
        @click="activeTab = def.status"
      >
        {{ def.label }}
        <span v-if="tabs[def.status].loaded" class="tab-count">{{ tabs[def.status].total }}</span>
      </button>
    </div>

    <!-- Loading (initial only) -->
    <div v-if="tabs[activeTab].loading && !tabs[activeTab].loaded" class="loading-state">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
      <p>Loading...</p>
    </div>

    <template v-else>
      <!-- Empty state -->
      <div v-if="tabs[activeTab].signals.length === 0" class="empty-state">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="empty-icon" />
        <h3>No signals</h3>
        <p>No {{ tabDefs.find(d => d.status === activeTab)?.label.toLowerCase() }} signals right now.</p>
      </div>

      <!-- Signal list -->
      <div v-else class="queue-list">
        <div
          v-for="s in tabs[activeTab].signals"
          :key="s.id"
          class="queue-row"
        >
          <div class="row-info">
            <div class="row-title-line">
              <span class="row-title">{{ s.title || truncateUrl(s.source_url) || 'Untitled' }}</span>
              <span
                class="status-badge"
                :class="statusConfig[s.status]?.class"
              >
                <font-awesome-icon
                  v-if="s.status === 1"
                  :icon="['fas', 'spinner']"
                  spin
                  style="font-size: 0.55rem"
                />
                {{ statusConfig[s.status]?.label }}
              </span>
            </div>

            <span v-if="s.error_message" class="row-reason">{{ s.error_message }}</span>

            <div class="row-meta">
              <span v-if="s.source_url" class="meta-url" :title="s.source_url">
                {{ truncateUrl(s.source_url) }}
              </span>
              <span class="meta-date">{{ formatRelativeDate(s.created_at) }}</span>
            </div>
          </div>

          <div class="row-actions">
            <button
              v-if="s.status === 3 || s.status === 4"
              class="btn-retry"
              @click="retrySignal(s)"
              :disabled="retryingId === s.id"
              title="Retry analysis"
            >
              <font-awesome-icon
                v-if="retryingId === s.id"
                :icon="['fas', 'spinner']"
                spin
              />
              <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-right']" />
            </button>
            <button
              v-if="canDelete(s)"
              class="btn-delete"
              @click="deleteSignal(s.id)"
              :disabled="deletingId === s.id"
              title="Delete signal"
            >
              <font-awesome-icon
                v-if="deletingId === s.id"
                :icon="['fas', 'spinner']"
                spin
              />
              <font-awesome-icon v-else :icon="['fas', 'trash']" />
            </button>
          </div>
        </div>

        <!-- Load more -->
        <button
          v-if="tabs[activeTab].signals.length < tabs[activeTab].total"
          class="btn-load-more"
          :disabled="tabs[activeTab].loading"
          @click="loadMore"
        >
          <font-awesome-icon v-if="tabs[activeTab].loading" :icon="['fas', 'spinner']" spin />
          <template v-else>Load more</template>
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.signal-queue {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: white;
  overflow-y: auto;
  padding: 1.5rem 2rem;
}

/* Tabs */
.queue-tabs {
  display: flex;
  align-items: center;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 1.25rem;
  flex-shrink: 0;
}

.queue-tab {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem 0.625rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.queue-tab:hover {
  color: #9ca3af;
}

.queue-tab.active {
  color: #c4b5fd;
  border-bottom-color: #8b5cf6;
}

.tab-count {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  min-width: 1.25rem;
  text-align: center;
}

.queue-tab.active .tab-count {
  background: rgba(139, 92, 246, 0.15);
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  color: #9ca3af;
}

.loading-icon {
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
}

.loading-state p {
  font-size: 0.8125rem;
  margin: 0;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  opacity: 0.3;
  margin-bottom: 1rem;
  color: #34d399;
}

.empty-state h3 {
  font-size: 1.125rem;
  color: #9ca3af;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.8125rem;
  margin: 0;
  max-width: 360px;
  line-height: 1.5;
}

/* Queue list */
.queue-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.queue-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.5rem;
  transition: all 0.15s ease;
}

.queue-row:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

/* Row info */
.row-info {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
  flex: 1;
}

.row-title-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.row-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-reason {
  font-size: 0.75rem;
  color: #9ca3af;
  line-height: 1.4;
}

.row-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.125rem;
}

.meta-url {
  font-size: 0.6875rem;
  color: #6b7280;
  font-family: 'Monaco', 'Courier New', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meta-date {
  font-size: 0.6875rem;
  color: #4b5563;
  white-space: nowrap;
}

/* Status badges */
.status-badge {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.1875rem 0.5rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
}

.status-pending {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.1);
}

.status-processing {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.1);
}

.status-failed {
  color: #f87171;
  background: rgba(248, 113, 113, 0.1);
}

.status-irrelevant {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.1);
}

/* Row actions */
.row-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

/* Retry button */
.btn-retry {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  color: #4b5563;
  cursor: pointer;
  font-size: 0.6875rem;
  transition: all 0.15s ease;
  margin-top: 0.125rem;
}

.btn-retry:hover:not(:disabled) {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.1);
  border-color: rgba(96, 165, 250, 0.2);
}

.btn-retry:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Delete button */
.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  color: #4b5563;
  cursor: pointer;
  font-size: 0.6875rem;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.btn-delete:hover:not(:disabled) {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Load more */
.btn-load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  margin-top: 0.5rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.375rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-load-more:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  color: #e5e7eb;
}

.btn-load-more:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Scrollbar */
.signal-queue::-webkit-scrollbar {
  width: 6px;
}

.signal-queue::-webkit-scrollbar-track {
  background: transparent;
}

.signal-queue::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}
</style>
